import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import type { ICCInfo } from '@curbnturf/entities';
import { AuthService } from '../auth/auth.service';
import { CreditCardApiService } from '../backend-api/credit-card-api.service';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

export interface IPaymentMethod {
  id: string;
  label: string;
  icon: string;
  brand: 'visa' | 'amex' | 'mastercard' | 'discover' | 'other';
  last4: string;
  isDefault: boolean;
  /** True when this card is backed by a real Converge-tokenized card on the
   *  backend (see `addRealCard`/`hydrateRealCard`) rather than the mock
   *  brand+last4 fields entered directly. */
  isReal?: boolean;
  /** Raw Converge expiry, `MMYY` (month not zero-padded — e.g. `'120'` is
   *  Jan '20, `'1230'` is Dec '30). Only set for real cards. */
  expire?: string;
  /** Result of the last `hydrateRealCard()` double-check against the server:
   *  `undefined` means not verified yet this session (treated as
   *  provisionally OK — see `hasValidRealCard` in booking-review), `true`/
   *  `false` is the last known result. */
  isActive?: boolean;
}

/** Parses a Converge `MMYY` expiry (month not zero-padded) and checks it
 *  against today. */
function isExpired(expire: string): boolean {
  const year = Number(`20${expire.slice(-2)}`);
  const month = Number(expire.slice(0, -2));
  if (!year || !month) return false; // Unparseable — don't falsely invalidate.
  const endOfMonth = new Date(year, month, 0, 23, 59, 59);
  return endOfMonth < new Date();
}

const REAL_CARD_ID = 'card-real';

function brandFromCcType(type: string | undefined): IPaymentMethod['brand'] {
  const t = (type || '').toLowerCase();
  if (t.includes('visa')) return 'visa';
  if (t.includes('amex') || t.includes('american')) return 'amex';
  if (t.includes('master')) return 'mastercard';
  if (t.includes('discover')) return 'discover';
  return 'other';
}

const PAYMENTS_KEY = 'cnt-payments';
const PAYMENTS_IDB_KEY = 'payments:cards';

@Injectable({ providedIn: 'root' })
export class PaymentMethodsService {
  private platformId = inject(PLATFORM_ID);
  private creditCardSvc = inject(CreditCardApiService);
  private auth = inject(AuthService);

  private readonly store = createIdbKvStore<IPaymentMethod[]>({
    legacyKeys: { [PAYMENTS_IDB_KEY]: PAYMENTS_KEY },
  });

  private readonly _methods$ = new BehaviorSubject<IPaymentMethod[]>([]);
  readonly methods$: Observable<IPaymentMethod[]> = this._methods$.asObservable();

  constructor() {
    this._methods$.next(this.read());
    if (isPlatformBrowser(this.platformId)) {
      // read() above likely returns [] pre-hydration (async IDB init hasn't
      // resolved yet) — re-emit once the store's cache actually reflects
      // any previously-saved cards.
      void this.store.init().then(() => this._methods$.next(this.read()));
      this.auth.loggedOut$.subscribe(() => this.write([]));
    }
  }

  /** Query the real backend for a saved card token and upsert it into the
   *  local list if found — this is the "double check against the server"
   *  that /booking/review relies on before letting a booking submit. Called
   *  once per page load for a real (backend-linked) signed-in user. */
  async hydrateRealCard(userId: number): Promise<void> {
    try {
      const ccInfo = await firstValueFrom(this.creditCardSvc.query(userId));
      if (ccInfo?.number) this.upsertRealCard(ccInfo);
    } catch (err) {
      // Both the real server (queryToken() -> NotFoundException, 404) and
      // payment-processing-mock (queryToken() -> BadRequestException, 400)
      // use their own status code for "no token on file" — treat either as
      // a genuine miss. A card we already believed was real needs to be
      // flagged inactive rather than silently left as-is (previously this
      // branch was indistinguishable from "no card yet", so a stale/dead
      // card just kept rendering as fine). Any other status or a network
      // error is transient — leave existing state untouched.
      const status = err instanceof HttpErrorResponse ? err.status : undefined;
      if (status === 404 || status === 400) {
        this.write(this._methods$.value.map(c => (c.id === REAL_CARD_ID ? { ...c, isActive: false } : c)));
      }
    }
  }

  /** Tokenize + save a real card via the 3-step Converge chain (request-token
   *  → hosted process-token → save-token), then upsert it as the default
   *  payment method. Throws on failure (invalid card / declined / network) —
   *  callers surface that as a toast/inline error. */
  async addRealCard(input: {
    userId: number;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zipcode: string;
    cardNumber: string;
    expMonth: string;
    expYear: string;
    cvv: string;
  }): Promise<IPaymentMethod> {
    const ccInfo = await firstValueFrom(
      this.creditCardSvc.save(
        {
          userId: input.userId,
          firstName: input.firstName,
          lastName: input.lastName,
          address: input.address,
          city: input.city,
          state: input.state,
          zipcode: input.zipcode,
        },
        {
          ssl_card_number: input.cardNumber.replace(/\s/g, ''),
          ssl_exp_date: `${input.expMonth}${input.expYear.slice(-2)}`,
          ssl_cvv2cvc2: input.cvv,
        },
      ),
    );
    return this.upsertRealCard(ccInfo);
  }

  private upsertRealCard(ccInfo: ICCInfo): IPaymentMethod {
    const brand = brandFromCcType(ccInfo.type);
    const last4 = (ccInfo.number || '').slice(-4);
    const brandLabel = brand.charAt(0).toUpperCase() + brand.slice(1);
    const card: IPaymentMethod = {
      id: REAL_CARD_ID,
      label: `${brandLabel} ending in ${last4}`,
      icon: 'credit_card',
      brand,
      last4,
      isDefault: true,
      isReal: true,
      expire: ccInfo.expire,
      isActive: !isExpired(ccInfo.expire),
    };
    const rest = this._methods$.value.filter(c => c.id !== REAL_CARD_ID).map(c => ({ ...c, isDefault: false }));
    this.write([card, ...rest]);
    return card;
  }

  list(): IPaymentMethod[] {
    return this._methods$.value;
  }

  /** Add a new mock card. Only the last 4 digits + brand are stored. */
  add(input: { brand: IPaymentMethod['brand']; last4: string; makeDefault?: boolean }): IPaymentMethod {
    const id = `card-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const brandLabel = input.brand.charAt(0).toUpperCase() + input.brand.slice(1);
    const card: IPaymentMethod = {
      id,
      label: `${brandLabel} ending in ${input.last4}`,
      icon: 'credit_card',
      brand: input.brand,
      last4: input.last4,
      isDefault: !!input.makeDefault,
    };
    let next = [...this._methods$.value, card];
    if (card.isDefault) next = next.map(c => ({ ...c, isDefault: c.id === id }));
    this.write(next);
    return card;
  }

  remove(id: string): void {
    const next = this._methods$.value.filter(c => c.id !== id);
    // If we removed the default, promote the first remaining card.
    if (next.length > 0 && !next.some(c => c.isDefault)) {
      next[0] = { ...next[0], isDefault: true };
    }
    this.write(next);
  }

  setDefault(id: string): void {
    const next = this._methods$.value.map(c => ({ ...c, isDefault: c.id === id }));
    this.write(next);
  }

  private read(): IPaymentMethod[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    return this.store.read(PAYMENTS_IDB_KEY) ?? [];
  }

  private write(methods: IPaymentMethod[]): void {
    if (isPlatformBrowser(this.platformId)) {
      void this.store.write(PAYMENTS_IDB_KEY, methods);
    }
    this._methods$.next(methods);
  }
}
